/** 弾いた理由。画面はこれを見て文言を出す（英語のメッセージを直接出さない）。 */
export type FormSchemaIssue =
  | "empty"
  | "invalidJson"
  | "notObject"
  | "invalidProperties"
  | "invalidRequired"
  | "unknownRequired";

/** 弾いた理由 → 文言のキー（フォーム定義の編集と取り込みで同じ文言を出す）。 */
export const FORM_SCHEMA_ISSUE_KEYS = {
  empty: "formDesign.issueEmpty",
  invalidJson: "formDesign.issueInvalidJson",
  notObject: "formDesign.issueNotObject",
  invalidProperties: "formDesign.issueInvalidProperties",
  invalidRequired: "formDesign.issueInvalidRequired",
  unknownRequired: "formDesign.issueUnknownRequired",
} as const satisfies Record<FormSchemaIssue, string>;

export type FormSchemaValidation =
  | { readonly ok: true; readonly value: unknown }
  | {
      readonly ok: false;
      readonly reason: FormSchemaIssue;
      /** 該当する項目名など、文言に差し込む補足。 */
      readonly detail: string | null;
    };

/**
 * 保存前にフォーム定義（JSON Schema / UI Schema）を検証する。
 *
 * JSON Schema 全体の妥当性までは見ない（RJSF が受け取る範囲は広く、厳しくすると
 * 正しい定義を弾く）。ここで見るのは「保存すると確実に壊れる形」だけ:
 * JSON として読めない / オブジェクトでない / properties や required の型が違う /
 * required に存在しない項目がある。
 *
 * `*-uischema.json` は JSON Schema ではないので properties が無くても通す。
 */
export function validateFormSchema(text: string): FormSchemaValidation {
  if (text.trim() === "") return fail("empty");

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return fail("invalidJson");
  }

  if (!isObject(parsed)) return fail("notObject");

  const { properties, required } = parsed;

  // properties が無いのは UI Schema。あるなら形を見る
  if (properties !== undefined && !isObject(properties)) {
    return fail("invalidProperties");
  }

  if (required !== undefined) {
    if (!Array.isArray(required)) return fail("invalidRequired");

    const known = isObject(properties) ? Object.keys(properties) : [];
    const missing = required.find(
      (key) => typeof key !== "string" || !known.includes(key),
    );
    if (missing !== undefined) {
      return fail("unknownRequired", String(missing));
    }
  }

  return { ok: true, value: parsed };
}

function fail(
  reason: FormSchemaIssue,
  detail: string | null = null,
): FormSchemaValidation {
  return { ok: false, reason, detail };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
